import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { Carousels } from '../components/carousels/component';
import { ProductCardSkeletonLoader } from '../components/productSkeleton/component';
import CarouselSkeletonV3 from '../components/productSkeleton/component/CarouselSkeletonV3';
import { config } from '../config/env';
import { useCarouselHook } from '../hooks/useCarouseHook';
import SendBrowserEvent from '../services/sendBrowserEvent.service';
import '../utils/i18n';
import { RecommendationType } from '../types/common';
import { getFeatureMinVersion, isFeatureEnabled } from '../utils/common.util';
import { FALLBACK_APP_VERSION, HORIZON_FONT_V2_CLASS_NAME } from '../utils/constants';
import { isFlagEnabledForCountry } from '../utils/helper';

export default function CarouselPage() {
  const [searchParams] = useSearchParams();
  const { carouselSettings, isInitialLoading, isPaginationLoading, fetchMoreRecommendation, shouldFetch } =
    useCarouselHook(searchParams);
  const enableHorizonProductCard = config.ENABLE_HORIZON_PRODUCT_CARD;
  const { countryCode: countryCodeFromParams, isNative, appVersion } = Object.fromEntries(searchParams.entries());
  const isNativeView = isNative?.toLocaleLowerCase() === 'true';
  const isHorizonProductCardEnabled = useMemo(() => {
    return Boolean(isFlagEnabledForCountry(countryCodeFromParams, enableHorizonProductCard) && isNativeView);
  }, [countryCodeFromParams, enableHorizonProductCard, isNativeView]);

  const isHorizonUiUxEnabled = useMemo(() => {
    const horizonUiUxMinVersion = getFeatureMinVersion(countryCodeFromParams, config.HORIZON_UI_UX_MIN_VERSION);
    return isHorizonProductCardEnabled && isFeatureEnabled(horizonUiUxMinVersion, appVersion || FALLBACK_APP_VERSION);
  }, [appVersion, countryCodeFromParams, isHorizonProductCardEnabled]);

  const type = searchParams.get('type');
  const isBasketRecommendation = type === RecommendationType.BASKET_RECOMMENDATION;
  const shouldUseHorizonFont = isHorizonUiUxEnabled && isBasketRecommendation;

  useEffect(() => {
    SendBrowserEvent.iframeReady(true, isBasketRecommendation, isHorizonUiUxEnabled, isHorizonUiUxEnabled);
  }, [isBasketRecommendation, isHorizonUiUxEnabled]);

  useEffect(() => {
    if (!shouldUseHorizonFont) return;

    document.documentElement.classList.add(HORIZON_FONT_V2_CLASS_NAME);
    document.body.classList.add(HORIZON_FONT_V2_CLASS_NAME);
    document.getElementById('root')?.classList.add(HORIZON_FONT_V2_CLASS_NAME);

    return () => {
      document.documentElement.classList.remove(HORIZON_FONT_V2_CLASS_NAME);
      document.body.classList.remove(HORIZON_FONT_V2_CLASS_NAME);
      document.getElementById('root')?.classList.remove(HORIZON_FONT_V2_CLASS_NAME);
    };
  }, [shouldUseHorizonFont]);

  const ProductCarouselSkeleton = isHorizonUiUxEnabled ? CarouselSkeletonV3 : ProductCardSkeletonLoader;

  return (
    <div className={shouldUseHorizonFont ? HORIZON_FONT_V2_CLASS_NAME : undefined}>
      {!isInitialLoading && carouselSettings ? (
        <Carousels
          {...carouselSettings}
          fetchMoreData={fetchMoreRecommendation}
          shouldFetch={shouldFetch}
          isHorizonProductCardEnabled={isHorizonUiUxEnabled}
          isPaginationLoading={isPaginationLoading}
        />
      ) : (
        <ProductCarouselSkeleton isHorizontal={!isHorizonUiUxEnabled} countryCode={countryCodeFromParams} />
      )}
    </div>
  );
}
